import { useEffect, useRef, useState } from "react";
import {
  Input,
  Row,
  Col,
  Button,
  Form,
  Collapse,
  Select,
  DatePicker,
  Tooltip,
  Typography,
  Checkbox,
  InputNumber,
  TimePicker,
  Card,
  Space,
} from "antd";
import {
  ActivityDetails,
  ActivityList,
  ContainerTypes,
  MlResponse,
  ShipmentHazmat,
  TMSPayloadRequest,
} from "redux/users/types";
import { BaseButtonsForm } from "components/common/forms/BaseButtonsForm";
import {
  mlItemsString,
  mlResponseString,
  stringConfig,
} from "config/site.string";
import {
  DATE_TIME_FORMAT_WITHOUT_SECONDS_24HRS,
  disablePastDates,
  getErrorMessageString,
  SEVER_DATE_TIME_FORMAT,
} from "library/helpers/utils";
import { FONT_SIZE_PX } from "config/theme/constants";
import dayjs from "dayjs";
import { notificationController } from "components/common/notificationController";
import apiService from "services/apiservice";
import PhoneInputComponent from "components/common/PhoneInputComponent";
import TextArea from "antd/es/input/TextArea";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import {
  PlusOutlined,
  DeleteOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  ErrorMessage,
  FormContainer,
  PDFFormContainer,
  PdfFormTitle,
  PDFFormWrapper,
  integratedCashCardBodyStyle,
  integratedCashCardStyle,
  integratedCashRowStyle,
  integratedCashSectionHeadingStyle,
} from "../styles";
import { ServiceLevel, serviceLevels } from "utils/utils";

dayjs.extend(utc);
dayjs.extend(timezone);
const { Panel } = Collapse;

export const WEIGHT_UNIT_OPTION = [
  {
    label: "LBs",
    value: "lb",
  },
  {
    label: "KGs",
    value: "kg",
  },
];

export const DIMENSION_UNIT_OPTION = [
  {
    label: "Inches",
    value: "in",
  },
  {
    label: "CMs",
    value: "cm",
  },
];
export interface Countries {
  id: string;
  country: string;
}

const COUNTRY_CODES: Countries[] = [
  { id: "MEX", country: "Mexico" },
  { id: "CAN", country: "Canada" },
  { id: "USA", country: "United States" },
];

interface UnitsType {
  label: string;
  value: string;
}
interface PdfPreviewProps {
  form: any;
  selectedRecord: ActivityList;
  isClose: boolean;
  handleOk: () => void;
  handleCancel: () => void;
  cancelText: string;
  saveText: string;
  onClose: any;
  setPending: any;
  hasViewAccess: boolean;
  hasEditAccess: boolean;
  excelSheetName?: string | null;
}
const { Option } = Select;

export const SelectDimensionsDropdown = (
  dimensionTypes: UnitsType[] | undefined,
  value: string | null,
  onChange: ((value: string) => void) | undefined,
  placeholder: string,
  isDisabled: boolean = false,
) => {
  return (
    <Select
      style={{ width: "100%" }}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      disabled={isDisabled}
    >
      {dimensionTypes?.map((type) => (
        <Option key={type.label} value={type.value}>
          {type.label}
        </Option>
      ))}
    </Select>
  );
};

export const SelectWeightDropdown = (
  weightTypes: UnitsType[] | undefined,
  value: string | null,
  onChange: ((value: string) => void) | undefined,
  placeholder: string,
  isDisabled: boolean = false,
) => {
  return (
    <Select
      style={{ width: "100%" }}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      disabled={isDisabled}
    >
      {weightTypes?.map((type) => (
        <Option key={type.label} value={type.value}>
          {type.label}
        </Option>
      ))}
    </Select>
  );
};

const PdfForm = ({
  form,
  selectedRecord,
  isClose,
  handleCancel,
  cancelText,
  saveText,
  onClose,
  setPending,
  hasEditAccess,
  handleOk,
}: PdfPreviewProps) => {
  const [details, setDetails] = useState<ActivityDetails>();
  const [containerTypes, setContainerTypes] = useState<ContainerTypes[]>();
  let weightTypes: UnitsType[] = WEIGHT_UNIT_OPTION;
  let dimensionTypes: UnitsType[] = DIMENSION_UNIT_OPTION;
  const [itemsLength, setItemsLength] = useState(0);
  const initialized = useRef(false);
  const initializedForm = useRef(false);
  const currentUser = JSON.parse(
    localStorage.getItem("loggedInUser") || "null",
  );
  const [isShipDateUtc, setIsShipDateUtc] = useState(false);
  const [isDeliveryDateUtc, setIsDeliveryDateUtc] = useState(false);
  const [isDeliveryCloseDateUtc, setIsDeliveryCloseDateUtc] = useState(false);
  const [isFacilityDateUtc, setIsFacilityDateUtc] = useState(false);
  const token = currentUser?.token;
  const [mobileShipToPhoneData, setMobileShipToPhoneData] = useState<any>({
    phoneValue: "",
  });
  const [mobileShipFromPhoneData, setMobileShipFromPhoneData] = useState<any>({
    phoneValue: "",
  });
  const fileName = selectedRecord.fileName?.split("/").pop() || "";
  const fileExtension = fileName.split(".").pop()?.toLowerCase() || "";
  const isExcel = ["xls", "xlsx"].includes(fileExtension);

  const delByValueBetweenBillTo = [
    "THECON",
    "LGELEC",
    "TIDELLP",
    "LGELECMKTG",
    "HPEDEMO",
    "SUPERIORP",
  ];
  const isReturnToDE = Form.useWatch("isReturnToDE", form);
  const isHazmatChecked = Form.useWatch("isHazmat", form);
  const HPESpecific =
    selectedRecord.billTo === "HPE" ||
    selectedRecord.billTo === "HPEDEMO" ||
    selectedRecord.billTo === "HEW057";
  const isModeUpdate = details?.mlResponse?.isUpdateShipment ?? false;
  const showDeliveryCloseTime =
    delByValueBetweenBillTo.includes(selectedRecord.billTo) ||
    (selectedRecord.billTo === "INTEGRATEDCASH" && isReturnToDE === false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await apiService.get(`/GetContainerTypes`, {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        setContainerTypes(data);
      } catch (e: any) {
        const errorMessages = getErrorMessageString(e);
        if (errorMessages !== undefined && e?.response?.status !== 401)
          notificationController.error({
            message: "Error",
            description: `Error fetching container types: ${errorMessages}`,
          });
      }
    };
    if (!initialized.current) {
      initialized.current = true;
      fetchData();
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await apiService.get(
          `/GetActivityDetails/${selectedRecord.activityId}`,
          {
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
            },
          },
        );
        setDetails(data);
      } catch (e: any) {
        const errorMessages = getErrorMessageString(e);
        if (errorMessages !== undefined && e?.response?.status !== 401)
          notificationController.error({
            message: "Error",
            description: `Error fetching activity details: ${errorMessages}`,
          });
      }
    };
    if (!initializedForm.current) {
      initializedForm.current = true;
      fetchData();
    }
  }, [selectedRecord, form]);

  const isZSuffix = (dateString: string): boolean => {
    return dateString && dateString.endsWith("Z") ? true : false;
  };

  useEffect(() => {
    if (details?.mlResponse) {
      setIsShipDateUtc(isZSuffix(details.mlResponse.shipDate));
      setIsDeliveryCloseDateUtc(
        isZSuffix(details.mlResponse.deliveryCloseTime),
      );
      setIsDeliveryDateUtc(isZSuffix(details.mlResponse.deliveryDateTime));
      setIsFacilityDateUtc(isZSuffix(details.mlResponse.facilityCloseTime));
      form.setFieldsValue(details?.mlResponse);
      form.setFieldsValue({ items: details?.mlResponse.items });
      form.setFieldsValue({
        facilityCloseTime:
          isZSuffix(details?.mlResponse?.facilityCloseTime) === true
            ? details?.mlResponse?.facilityCloseTime
              ? dayjs(details?.mlResponse?.facilityCloseTime).utc()
              : undefined
            : details?.mlResponse?.facilityCloseTime
              ? dayjs(details?.mlResponse?.facilityCloseTime)
              : undefined,
      });
      form.setFieldsValue(details?.mlResponse.shipmentHazmat);
      form.setFieldsValue({
        shipDate:
          isZSuffix(details?.mlResponse?.shipDate) === true
            ? details?.mlResponse?.shipDate
              ? dayjs(details?.mlResponse?.shipDate).utc()
              : undefined
            : details?.mlResponse?.shipDate
              ? dayjs(details?.mlResponse?.shipDate)
              : undefined,
      });
      form.setFieldsValue({
        deliveryDateTime:
          isZSuffix(details?.mlResponse?.deliveryDateTime) === true
            ? details?.mlResponse?.deliveryDateTime
              ? dayjs(details?.mlResponse?.deliveryDateTime).utc()
              : undefined
            : details?.mlResponse?.deliveryDateTime
              ? dayjs(details?.mlResponse?.deliveryDateTime)
              : undefined,
      });
      form.setFieldsValue({
        deliveryCloseTime:
          isZSuffix(details?.mlResponse?.deliveryCloseTime) === true
            ? details?.mlResponse?.deliveryCloseTime
              ? dayjs(details?.mlResponse?.deliveryCloseTime).utc()
              : undefined
            : details?.mlResponse?.deliveryCloseTime
              ? dayjs(details?.mlResponse?.deliveryCloseTime)
              : undefined,
      });
      const mobileShipToPhone = `${
        details?.mlResponse?.shipToCustomerPhoneExt
          ? details?.mlResponse?.shipToCustomerPhoneExt?.toString()
          : 1
      }${details?.mlResponse?.shipToCustomerPhone?.toString()}`;
      setMobileShipToPhoneData({
        ...mobileShipToPhoneData,
        phoneNumber: details?.mlResponse?.shipToCustomerPhone?.toString(),
        phoneExt: details?.mlResponse?.shipToCustomerPhoneExt?.toString(),
        phoneValue: mobileShipToPhone?.toString(),
      });

      const mobileShipFromPhone = `${
        details?.mlResponse?.shipFromPhoneExt
          ? details?.mlResponse?.shipFromPhoneExt?.toString()
          : 1
      }${details?.mlResponse?.shipFromPhone?.toString()}`;
      setMobileShipFromPhoneData({
        ...mobileShipFromPhoneData,
        phoneNumber: details?.mlResponse?.shipFromPhone?.toString(),
        phoneExt: details?.mlResponse?.shipFromPhoneExt?.toString(),
        phoneValue: mobileShipFromPhone?.toString(),
      });
      setItemsLength(details?.mlResponse?.items?.length);
      form.setFieldsValue({
        items: details?.mlResponse.items?.map((item: any) => ({
          ...item,
          itemWeightUnit: item.itemWeightUnit ?? "lb",
          itemDimensionUnit: item.itemDimensionUnit ?? "in",
        })),
      });
    } else {
      form.resetFields();
    }
  }, [details, form]);

  const SelectContainerDropdown = (
    containerTypes: ContainerTypes[] | undefined,
    value: string | undefined,
    onChange: ((value: string | undefined) => void) | undefined,
    placeholder: string,
    allowClear: boolean = false,
  ) => {
    return (
      <Select
        style={{ width: "100%" }}
        placeholder={placeholder}
        {...(value !== undefined ? { value } : {})}
        {...(onChange !== undefined ? { onChange } : {})}
        allowClear={allowClear}
      >
        {containerTypes?.map((type) => (
          <Option key={type.containerTypeCode} value={type.containerCode}>
            {type.containerTypeDescription}
          </Option>
        ))}
      </Select>
    );
  };
  const SelectServiceLevelDropdown = (
    serviceLevels: ServiceLevel[] | undefined,
    value: string | null,
    onChange: ((value: string) => void) | undefined,
    placeholder: string,
  ) => {
    return (
      <Select
        style={{ width: "100%" }}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        options={serviceLevels?.map((level) => ({
          value: level.serviceLevelCode,
          label: level.serviceLevelDescription,
        }))}
      />
    );
  };
  const SelectCountryDropdown = (
    countryTypes: Countries[] | undefined = COUNTRY_CODES,
    value: string | null | undefined,
    onChange: ((value: string) => void) | undefined,
    placeholder: string,
    isDisabled: boolean = false,
  ) => {
    return (
      <Select
        style={{ width: "100%" }}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        disabled={isDisabled}
      >
        {countryTypes?.map((type) => (
          <Select.Option key={type.id} value={type.country}>
            {type.country}
          </Select.Option>
        ))}
      </Select>
    );
  };

  const addItem = () => {
    const items = form.getFieldValue("items") || [];
    const nextIndex = items.length;
    form.setFieldsValue({
      items: [
        ...items,
        {
          itemName: `Line Item ${nextIndex + 1}`,
          itemDescription: "",
          itemQuantity: null,
          itemWeight: null,
          itemContainerType: "CTN",
          itemWeightUnit: "lb",
          itemLength: null,
          itemWidth: null,
          itemHeight: null,
          itemDimensionUnit: "in",
        },
      ],
    });
    setItemsLength(items.length + 1);
  };

  const handleDelete = (remove: any, fieldName: any) => {
    remove(fieldName);
  };

  const handleSubmit = (values: any) => {
    const shipmentHazmat: ShipmentHazmat = {
      goodsName: values.goodsName,
      goodsClass: values.goodsClass,
      emergencyContact: values.emergencyContact,
    };
    const mlResponseValues: MlResponse = {
      shipment: values.shipment,
      customerPO: values.customerPO,
      po: values.po,
      costCenter: values.costCenter,
      rmaNumber: values.rmaNumber,
      safeNo: values.safeNo,
      shipperReference: values.shipperReference,
      serialNumber: values.serialNumber,
      billingCode: values.billingCode,
      bolNo: values.bolNo,
      consignment: values.consignment,
      formatDescription: values.formatDescription,
      hpePackId: values.hpePackId,
      customerNo: values.customerNo,
      salesOrder: values.salesOrder,
      transferNo: values.transferNo,
      deliveryOrder: values.deliveryOrder,
      housebill: values.housebill === "" ? null : values.housebill,
      trailerNo: values.trailerNo === "" ? null : values.trailerNo,
      storeNo: values.storeNo,
      orderNumber: values.orderNumber,
      proNumber: values.proNumber,
      refNo: values.refNo,
      carrier: values.carrier,
      shipDate: isShipDateUtc
        ? values.shipDate?.utc().format(SEVER_DATE_TIME_FORMAT)
        : values.shipDate?.format(SEVER_DATE_TIME_FORMAT),
      deliveryDateTime: isDeliveryDateUtc
        ? values.deliveryDateTime?.utc().format(SEVER_DATE_TIME_FORMAT)
        : values.deliveryDateTime?.format(SEVER_DATE_TIME_FORMAT),
      deliveryCloseTime:
        values.isReturnToDE === true &&
        selectedRecord.billTo === "INTEGRATEDCASH"
          ? null
          : isDeliveryCloseDateUtc
            ? values.deliveryCloseTime?.utc().format(SEVER_DATE_TIME_FORMAT)
            : values.deliveryCloseTime?.format(SEVER_DATE_TIME_FORMAT),
      facilityCloseTime: isFacilityDateUtc
        ? values.facilityCloseTime?.utc().format(SEVER_DATE_TIME_FORMAT)
        : values.facilityCloseTime?.format(SEVER_DATE_TIME_FORMAT),
      //   facilityCloseTime: values.facilityCloseTime
      //   ? values.facilityCloseTime.format("HH:mm:ss") // or just "HH:mm" if seconds not needed
      //   : null,
      shipperCode: values.shipperCode,
      declaredValue: values.declaredValue,
      shipFromName: values.shipFromName,
      shipFromAddress1: values.shipFromAddress1,
      shipFromAddress2: values.shipFromAddress2,
      shipFromCity: values.shipFromCity,
      shipFromState: values.shipFromState,
      shipFromZip: values.shipFromZip,
      shipFromEmail: values?.shipFromEmail,
      isHazmat: values.isHazmat,
      isScrap: values.isScrap,
      isPiaggioInterGlobe: values.isPiaggioInterGlobe,
      isVessel: values.isVessel,
      specialInstruction: values.specialInstruction,
      pickupInstructions: values.pickupInstructions,
      shipFromCountry: values.shipFromCountry,
      shipFromPhone:
        mobileShipFromPhoneData.phoneNumber === ""
          ? null
          : mobileShipFromPhoneData.phoneNumber,
      shipFromPhoneExt:
        mobileShipFromPhoneData.phoneExt === ""
          ? null
          : mobileShipFromPhoneData.phoneExt,
      shipFromContact: values.shipFromContact,
      shipperNo: values.shipperNo,
      consigneeNo: values.consigneeNo,
      shipToCustomerName: values.shipToCustomerName,
      shipToCustomerAddress1: values.shipToCustomerAddress1,
      shipToCustomerAddress2: values.shipToCustomerAddress2,
      shipToCustomerCity: values.shipToCustomerCity,
      shipToCustomerState: values.shipToCustomerState,
      shipToCustomerZip: values.shipToCustomerZip,
      shipToCustomerCountry: values.shipToCustomerCountry,
      shipToCustomerPhone:
        mobileShipToPhoneData.phoneNumber === ""
          ? null
          : mobileShipToPhoneData.phoneNumber,
      shipToCustomerPhoneExt:
        mobileShipToPhoneData.phoneExt === ""
          ? null
          : mobileShipToPhoneData.phoneExt,
      shipToCustomerContact: values.shipToCustomerContact,
      shipToCustomerEmail: values.shipToCustomerEmail,
      consigneeReference: values.consigneeReference,

      items: values.items?.map((item: any) => ({
        ...item,
        itemName:
          item.itemName === null || item.itemName === ""
            ? item.itemDescription
            : item.itemName,
        itemLength: item.itemLength,
        itemWidth: item.itemWidth,
        itemHeight: item.itemHeight,
        itemWeight: item.itemWeight,
        itemQuantity: item.itemQuantity,
        hazmat: item.hazmat,
      })),
      isShipDateOnly: details?.mlResponse?.isShipDateOnly,
      isReturnToDE: values.isReturnToDE,
      isReturnToNJ: values.isReturnToNJ,
      isPickupFromDE: values.isPickupFromDE,
      isPickupFromNJ: values.isPickupFromNJ,
      isRma: values.isRma,
      isDropoff: values.isDropoff,
      isPickup: values.isPickup,
      isDeliveryDateOnly: details?.mlResponse?.isDeliveryDateOnly,
      serviceLevel: values.serviceLevel,
      isDeliveryCloseDateOnly: details?.mlResponse?.isDeliveryCloseDateOnly,
      pickupLiftGate: details?.mlResponse?.pickupLiftGate,
      pickupInside: details?.mlResponse?.pickupInside,
      notifyEmailId: details?.mlResponse?.notifyEmailId,
      isSpecialEquipment: values.isSpecialEquipment,
      shipmentType: details?.mlResponse?.shipmentType,
      isUpdateShipment: details?.mlResponse?.isUpdateShipment ?? false,
      isDeliveryDateTimeAm: true,
      isShipDateTimeAm: true,
      isDeliveryCloseTimeAm: true,
      isFacilityCloseTimeAm: true,
      shipmentHazmat: shipmentHazmat,
    };
    const payload: TMSPayloadRequest = {
      activityId: selectedRecord?.activityId,
      emailLogAttachmentId: selectedRecord?.emailLogAttachmentId,
      mlStatus: 3,
      tmsStatus: 1,
      pageNumber: selectedRecord?.pageNumber,
      mlLog: details?.mlLog,
      mlResponse: mlResponseValues,
      userId: currentUser.userDetails.userId,
      billTo: selectedRecord?.billTo,
    };

    const postMlResponseData = async () => {
      try {
        setPending(true);
        await apiService.post("/UpdateActivityMLResponse", payload, {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        });
        onClose();
        notificationController.success({
          message: "Success",
          description: `TMS Add domestic pickup updated successfully`,
        });
      } catch (e: any) {
        const errorMessages = getErrorMessageString(e);
        if (errorMessages !== undefined && e?.response?.status !== 401)
          notificationController.error({
            message: "Error",
            description: `${errorMessages}`,
          });
      } finally {
        setPending(false);
        handleOk();
      }
    };
    if (values.items?.length >= 1) {
      postMlResponseData();
    } else {
      notificationController.error({
        message: "Error",
        description: "Please enter at least one shipment line item.",
      });
      return;
    }
  };

  const scrollToFirstError = (errorInfo: { errorFields: any[] }) => {
    const errorFields = errorInfo.errorFields;
    if (errorFields.length) {
      const firstError = errorFields[0];
      const fieldPath = firstError.name;
      const isListItem = fieldPath.length > 1;

      let elementId = "";
      if (isListItem) {
        elementId = `pdfForm_${fieldPath.join("_")}`;
      } else {
        elementId = `pdfForm_${fieldPath[0]}`;
      }

      const errorElement = document.querySelector(
        `[id="${elementId}"], [id="${elementId}_0"]`,
      ) as HTMLElement;

      if (errorElement) {
        const panels = [];
        let currentPanel: any = errorElement.closest(".ant-collapse-item");

        while (currentPanel) {
          if (!currentPanel.classList.contains("ant-collapse-item-active")) {
            panels.push(currentPanel);
          }
          currentPanel =
            currentPanel.parentElement?.closest(".ant-collapse-item");
        }

        if (panels.length > 0) {
          panels.reverse().forEach((panel, index) => {
            const header = panel.querySelector(
              ".ant-collapse-header",
            ) as HTMLElement;
            header?.click();

            if (index === panels.length - 1) {
              setTimeout(
                () => {
                  errorElement.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  });
                  const inputElement = errorElement.querySelector(
                    "input.form-control, input, textarea, select",
                  ) as HTMLElement;
                  if (inputElement) {
                    inputElement.focus();
                  }
                },
                100 * (panels.length + 1),
              );
            }
          });
        } else {
          setTimeout(() => {
            errorElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
            const inputElement = errorElement.querySelector(
              "input.form-control, input, textarea, select",
            ) as HTMLElement;
            if (inputElement) {
              inputElement.focus();
            }
          }, 0);
        }
      }
    }
  };

  const onFinishFailed = (errorInfo: { errorFields: any }) => {
    scrollToFirstError(errorInfo);
  };

  return (
    <>
      <PDFFormContainer>
        <BaseButtonsForm
          form={form}
          name="pdfForm"
          layout="horizontal"
          initialValues={details?.mlResponse}
          onFinish={handleSubmit}
          onFinishFailed={onFinishFailed}
          id="pdfForm"
        >
          <FormContainer>
            <PdfFormTitle>
              Order Entry Details
              {selectedRecord.tmsError !== null && (
                <Tooltip title={selectedRecord.tmsError}>
                  <Typography.Link
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <ErrorMessage>
                      <WarningOutlined style={{ cursor: "pointer" }} />
                      TMS Error
                    </ErrorMessage>
                  </Typography.Link>
                </Tooltip>
              )}
            </PdfFormTitle>
            <PDFFormWrapper>
              <Collapse>
                <Panel
                  header="References"
                  key="1"
                  style={{ backgroundColor: "#f1f5fc" }}
                  forceRender
                >
                  <Row gutter={{ xs: 10, md: 15, xl: 30 }}>
                    <Col span={24}>
                      <BaseButtonsForm.Item
                        name="shipment"
                        label={<>{mlResponseString.shipment}</>}
                        labelCol={{ span: 24 }}
                      >
                        <Input disabled={isModeUpdate} />
                      </BaseButtonsForm.Item>
                    </Col>

                    <Col span={24}>
                      <BaseButtonsForm.Item
                        name="customerPO"
                        label={<>{mlResponseString.customerPO}</>}
                        labelCol={{ span: 24 }}
                      >
                        <Input disabled={isModeUpdate} />
                      </BaseButtonsForm.Item>
                    </Col>

                    <Col span={24}>
                      <BaseButtonsForm.Item
                        name="salesOrder"
                        label={<>{mlResponseString.salesOrder}</>}
                        labelCol={{ span: 24 }}
                      >
                        <Input disabled={isModeUpdate} />
                      </BaseButtonsForm.Item>
                    </Col>
                    {(selectedRecord.billTo === "NAUTILUS" ||
                      selectedRecord.billTo === "NAUTILUSEXP" ||
                      selectedRecord.billTo === "NAUTILUSFI") && (
                      <Col span={24}>
                        <BaseButtonsForm.Item
                          name="transferNo"
                          label={<>{mlResponseString.transferNo}</>}
                          labelCol={{ span: 24 }}
                        >
                          <Input />
                        </BaseButtonsForm.Item>
                      </Col>
                    )}
                    {selectedRecord.billTo !== "OLYMPUS" && (
                      <>
                        <Col span={24}>
                          <BaseButtonsForm.Item
                            name="deliveryOrder"
                            label={<>{mlResponseString.deliveryOrder}</>}
                            labelCol={{ span: 24 }}
                          >
                            <Input disabled={isModeUpdate} />
                          </BaseButtonsForm.Item>
                        </Col>
                      </>
                    )}
                    {(selectedRecord.billTo === "ECOLABINC" ||
                      selectedRecord.billTo === "NAUTILUSFI" ||
                      selectedRecord.billTo === "NAUTILUSEXP" ||
                      selectedRecord.billTo === "NAUTILUS" ||
                      selectedRecord.billTo === "LOZIERCORP" ||
                      selectedRecord.billTo === "COMPUCOM" ||
                      selectedRecord.billTo === "TIDELLP" ||
                      selectedRecord.billTo === "CARDTRONICS") && (
                      <Col span={24}>
                        <BaseButtonsForm.Item
                          name="refNo"
                          label={<>{mlResponseString.refNo}</>}
                          labelCol={{ span: 24 }}
                        >
                          <Input disabled={isModeUpdate} />
                        </BaseButtonsForm.Item>
                      </Col>
                    )}
                    {(selectedRecord.billTo === "CARDTRONICS" ||
                      selectedRecord.billTo === "NCRCORP" ||
                      selectedRecord.billTo === "CALIXNETWORKS" ||
                      HPESpecific) && (
                      <Col span={24}>
                        <BaseButtonsForm.Item
                          name="orderNumber"
                          label={<>{mlResponseString.orderNumber}</>}
                          labelCol={{ span: 24 }}
                        >
                          <Input disabled={isModeUpdate} />
                        </BaseButtonsForm.Item>
                      </Col>
                    )}
                    {(selectedRecord.billTo === "ECOLABINC" ||
                      selectedRecord.billTo === "NAUTILUSFI" ||
                      selectedRecord.billTo === "NAUTILUSEXP" ||
                      selectedRecord.billTo === "NAUTILUS" ||
                      selectedRecord.billTo === "LOZIERCORP" ||
                      selectedRecord.billTo === "HEW057" ||
                      selectedRecord.billTo === "HPE") && (
                      <>
                        <Col span={24}>
                          <BaseButtonsForm.Item
                            name="shipperReference"
                            label={<>{mlResponseString.shipperRef}</>}
                            labelCol={{ span: 24 }}
                          >
                            <Input disabled={isModeUpdate} />
                          </BaseButtonsForm.Item>
                        </Col>
                        <Col span={24}>
                          <BaseButtonsForm.Item
                            name="consigneeReference"
                            label={<>{mlResponseString.consigneeRef}</>}
                            labelCol={{ span: 24 }}
                          >
                            <Input disabled={isModeUpdate} />
                          </BaseButtonsForm.Item>
                        </Col>
                      </>
                    )}
                    {selectedRecord.billTo === "INTEGRATEDCASH" && (
                      <>
                        <Col span={24}>
                          <BaseButtonsForm.Item
                            name="safeNo"
                            label={<>{mlResponseString.safeNumber}</>}
                            labelCol={{ span: 24 }}
                          >
                            <Input />
                          </BaseButtonsForm.Item>
                        </Col>
                      </>
                    )}
                    {(selectedRecord.billTo === "PIAGRO" ||
                      selectedRecord.billTo === "CARDTRONICS") && (
                      <>
                        <Col span={24}>
                          <BaseButtonsForm.Item
                            name="serialNumber"
                            label={<>{mlResponseString.serialNumber}</>}
                            labelCol={{ span: 24 }}
                          >
                            <Input />
                          </BaseButtonsForm.Item>
                        </Col>
                        <Col span={24}>
                          <BaseButtonsForm.Item
                            name="consigneeReference"
                            label={<>{mlResponseString.consigneeRef}</>}
                            labelCol={{ span: 24 }}
                          >
                            <Input />
                          </BaseButtonsForm.Item>
                        </Col>
                      </>
                    )}
                    <Col span={24}>
                      <BaseButtonsForm.Item
                        name="carrier"
                        label={<>{mlResponseString.carrier}</>}
                        labelCol={{ span: 24 }}
                      >
                        <Input disabled={isModeUpdate} />
                      </BaseButtonsForm.Item>
                    </Col>

                    {(selectedRecord.billTo === "OLYMPUS" ||
                      selectedRecord.billTo === "LGELEC" ||
                      selectedRecord.billTo === "LGELECMKTG" ||
                      selectedRecord.billTo === "INTEGRATEDCASH" ||
                      selectedRecord.billTo === "SUPERIORP" ||
                      selectedRecord.billTo === "TIDELLP" ||
                      selectedRecord.billTo === "PIAGRO" ||
                      selectedRecord.billTo === "MICTEC" ||
                      selectedRecord.billTo === "AGMAUTO" ||
                      selectedRecord.billTo === "CALIX" ||
                      selectedRecord.billTo === "HPE" ||
                      selectedRecord.billTo === "HPEDEMO" ||
                      selectedRecord.billTo === "QPHARMA") && (
                      <Col span={24}>
                        <BaseButtonsForm.Item
                          name="po"
                          label={<>{mlResponseString.po}</>}
                          labelCol={{ span: 24 }}
                        >
                          <Input disabled={isModeUpdate} />
                        </BaseButtonsForm.Item>
                      </Col>
                    )}
                    {selectedRecord.billTo === "HPEDEMO" && (
                      <Col span={24}>
                        <BaseButtonsForm.Item
                          name="costCenter"
                          label={<>{mlResponseString.costCenter}</>}
                          labelCol={{ span: 24 }}
                        >
                          <Input />
                        </BaseButtonsForm.Item>
                      </Col>
                    )}
                    {(selectedRecord.billTo === "CALIX" ||
                      selectedRecord.billTo === "CARDTRONICS") && (
                      <Col span={24}>
                        <BaseButtonsForm.Item
                          name="rmaNumber"
                          label={<>{mlResponseString.rma}</>}
                          labelCol={{ span: 24 }}
                        >
                          <Input />
                        </BaseButtonsForm.Item>
                      </Col>
                    )}
                    {selectedRecord.billTo === "OLYMPUS" && (
                      <>
                        <Col span={24}>
                          <BaseButtonsForm.Item
                            name="billingCode"
                            label={<>{mlResponseString.billingCode}</>}
                            labelCol={{ span: 24 }}
                          >
                            <Input />
                          </BaseButtonsForm.Item>
                        </Col>
                        <Col span={24}>
                          <BaseButtonsForm.Item
                            name="consignment"
                            label={<>{mlResponseString.consignment}</>}
                            labelCol={{ span: 24 }}
                          >
                            <Input />
                          </BaseButtonsForm.Item>
                        </Col>
                      </>
                    )}
                    {HPESpecific && (
                      <>
                        <Col span={24}>
                          <BaseButtonsForm.Item
                            name="formatDescription"
                            label={<>{mlResponseString.formatDescription}</>}
                            labelCol={{ span: 24 }}
                          >
                            <Input disabled={isModeUpdate} />
                          </BaseButtonsForm.Item>
                        </Col>
                        <Col span={24}>
                          <BaseButtonsForm.Item
                            name="hpePackId"
                            label={<>{mlResponseString.hpePackId}</>}
                            labelCol={{ span: 24 }}
                          >
                            <Input disabled={isModeUpdate} />
                          </BaseButtonsForm.Item>
                        </Col>
                      </>
                    )}
                    {(selectedRecord.billTo === "TIDELLP" ||
                      selectedRecord.billTo === "NAUTILUS" ||
                      selectedRecord.billTo === "NAUTILUSEXP" ||
                      selectedRecord.billTo === "NAUTILUSFI" ||
                      HPESpecific) && (
                      <Col span={24}>
                        <BaseButtonsForm.Item
                          name="bolNo"
                          label={<>{mlResponseString.bolNo}</>}
                          labelCol={{ span: 24 }}
                        >
                          <Input disabled={isModeUpdate} />
                        </BaseButtonsForm.Item>
                      </Col>
                    )}
                    {(HPESpecific ||
                      selectedRecord.billTo === "LGELEC" ||
                      selectedRecord.billTo === "LGELECMKTG" ||
                      selectedRecord.billTo === "INTEGRATEDCASH" ||
                      selectedRecord.billTo === "ECOLABINC") && (
                      <Col span={24}>
                        <BaseButtonsForm.Item
                          name="customerNo"
                          label={<>{mlResponseString.customerNo}</>}
                          labelCol={{ span: 24 }}
                        >
                          <Input disabled={isModeUpdate} />
                        </BaseButtonsForm.Item>
                      </Col>
                    )}
                    {(HPESpecific ||
                      selectedRecord.billTo === "COMPUCOM" ||
                      selectedRecord.billTo === "LGELEC" ||
                      selectedRecord.billTo === "LGELECMKTG" ||
                      selectedRecord.billTo === "INTEGRATEDCASH" ||
                      selectedRecord.billTo === "TIDELLP" ||
                      selectedRecord.billTo === "LOZIERCORP" ||
                      selectedRecord.billTo === "ECOLABINC" ||
                      selectedRecord.billTo === "NAUTILUS" ||
                      selectedRecord.billTo === "NAUTILUSEXP" ||
                      selectedRecord.billTo === "NAUTILUSFI" ||
                      selectedRecord.billTo === "HSGLOBAL" ||
                      selectedRecord.billTo === "NCRCORP") && (
                      <Col span={24}>
                        <BaseButtonsForm.Item
                          name="housebill"
                          label={<>{mlResponseString.housebill}</>}
                          labelCol={{ span: 24 }}
                        >
                          <Input />
                        </BaseButtonsForm.Item>
                      </Col>
                    )}
                    {selectedRecord.billTo === "LOZIERCORP" && (
                      <>
                        <Col span={24}>
                          <BaseButtonsForm.Item
                            name="trailerNo"
                            label={<>{mlResponseString.trailerNo}</>}
                            labelCol={{ span: 24 }}
                          >
                            <Input />
                          </BaseButtonsForm.Item>
                        </Col>
                        <Col span={24}>
                          <BaseButtonsForm.Item
                            name="storeNo"
                            label={<>{mlResponseString.storeNo}</>}
                            labelCol={{ span: 24 }}
                          >
                            <Input />
                          </BaseButtonsForm.Item>
                        </Col>
                      </>
                    )}
                    <Col span={24}>
                      <BaseButtonsForm.Item
                        name="proNumber"
                        label={<>{mlResponseString.proNumber}</>}
                        labelCol={{ span: 24 }}
                      >
                        <Input disabled={isModeUpdate} />
                      </BaseButtonsForm.Item>
                    </Col>
                    {(selectedRecord.billTo === "LGELEC" ||
                      selectedRecord.billTo === "LGELECMKTG" ||
                      selectedRecord.billTo === "INTEGRATEDCASH" ||
                      selectedRecord.billTo === "TIDELLP") && (
                      <Col span={24}>
                        <BaseButtonsForm.Item
                          name="declaredValue"
                          label={<>{mlResponseString.declaredValue}</>}
                          labelCol={{ span: 24 }}
                        >
                          <InputNumber
                            style={{ width: "100%" }}
                            placeholder="0.00"
                            precision={2}
                            min={0}
                          />
                        </BaseButtonsForm.Item>
                      </Col>
                    )}
                    <Col span={24}>
                      <BaseButtonsForm.Item
                        label={<>{mlResponseString.shipDate}</>}
                        labelCol={{ span: 24 }}
                        name="shipDate"
                        rules={[
                          {
                            required: !isModeUpdate,
                            message: "Ship date is required",
                          },
                        ]}
                      >
                        <DatePicker
                          style={{ width: "100%" }}
                          format={DATE_TIME_FORMAT_WITHOUT_SECONDS_24HRS}
                          placeholder="Select Date"
                          allowClear={false}
                          inputReadOnly={true}
                          disabledDate={(current) => disablePastDates(current)}
                          disabled={isModeUpdate}
                          showTime
                        />
                      </BaseButtonsForm.Item>{" "}
                    </Col>

                    <Col span={24}>
                      <Form.Item
                        label="Facility Close Time"
                        name="facilityCloseTime"
                        labelCol={{ span: 24 }}
                        rules={[
                          {
                            required: !isModeUpdate,
                            message: "Facility close time is required",
                          },
                        ]}
                      >
                        <TimePicker
                          format="HH:mm"
                          style={{ width: "100%" }}
                          disabled={isModeUpdate}
                        />
                      </Form.Item>
                    </Col>

                    <Col span={24}>
                      <BaseButtonsForm.Item
                        label="Delivery Date Time"
                        labelCol={{ span: 24 }}
                        name="deliveryDateTime"
                        rules={[
                          {
                            required: !isModeUpdate,
                            message: "Delivery date time is required",
                          },
                        ]}
                      >
                        <DatePicker
                          style={{ width: "100%" }}
                          format={DATE_TIME_FORMAT_WITHOUT_SECONDS_24HRS}
                          placeholder="Select Date"
                          allowClear={false}
                          inputReadOnly={true}
                          disabledDate={(current) => disablePastDates(current)}
                          disabled={isModeUpdate}
                          showTime
                        />
                      </BaseButtonsForm.Item>{" "}
                    </Col>
                    {showDeliveryCloseTime && (
                      <Col span={24}>
                        <BaseButtonsForm.Item
                          label="Delivery Close Time"
                          labelCol={{ span: 24 }}
                          name="deliveryCloseTime"
                          rules={[
                            {
                              required: isModeUpdate
                                ? false
                                : showDeliveryCloseTime,
                              message: "Delivery close time is required",
                            },
                          ]}
                        >
                          <DatePicker
                            style={{ width: "100%" }}
                            format={DATE_TIME_FORMAT_WITHOUT_SECONDS_24HRS}
                            placeholder="Select Date"
                            showTime
                            allowClear={false}
                            disabledDate={(current) =>
                              disablePastDates(current)
                            }
                            disabled={isModeUpdate}
                            inputReadOnly={true}
                          />
                        </BaseButtonsForm.Item>{" "}
                      </Col>
                    )}
                    {selectedRecord.billTo === "HPE" && (
                      <Col span={24}>
                        <BaseButtonsForm.Item
                          name="serviceLevel"
                          label="Service Level"
                          labelCol={{ span: 24 }}
                        >
                          {SelectServiceLevelDropdown(
                            serviceLevels,
                            "5D",
                            undefined,
                            stringConfig.select,
                          )}
                        </BaseButtonsForm.Item>
                      </Col>
                    )}
                    {selectedRecord.billTo === "PIAGRO" && (
                      <Col span={24}>
                        <BaseButtonsForm.Item
                          name="isPiaggioInterGlobe"
                          valuePropName="checked"
                        >
                          <Checkbox>
                            {mlResponseString.isPiaggioInterGlobe}
                          </Checkbox>
                        </BaseButtonsForm.Item>
                      </Col>
                    )}
                    {selectedRecord.billTo === "CARDTRONICS" && isExcel && (
                      <Col span={24}>
                        <Card
                          size="small"
                          style={integratedCashCardStyle}
                          bodyStyle={integratedCashCardBodyStyle}
                        >
                          <Typography.Text
                            style={integratedCashSectionHeadingStyle}
                          >
                            {mlResponseString.shipmentType}
                          </Typography.Text>
                          <Row gutter={[12, 12]} style={integratedCashRowStyle}>
                            <Col span={12}>
                              <Space size="small" align="center">
                                <BaseButtonsForm.Item
                                  name="isRma"
                                  valuePropName="checked"
                                  noStyle
                                >
                                  <Checkbox
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        form.setFieldsValue({
                                          isPickup: false,
                                          isDropoff: false,
                                        });
                                      }
                                    }}
                                  >
                                    {mlResponseString.isRMA}
                                  </Checkbox>
                                </BaseButtonsForm.Item>
                              </Space>
                            </Col>
                            <Col span={12}>
                              <Space size="small" align="center">
                                <BaseButtonsForm.Item
                                  name="isPickup"
                                  valuePropName="checked"
                                  noStyle
                                >
                                  <Checkbox
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        form.setFieldsValue({
                                          isRma: false,
                                          isDropoff: false,
                                        });
                                      }
                                    }}
                                  >
                                    {mlResponseString.pickup}
                                  </Checkbox>
                                </BaseButtonsForm.Item>
                              </Space>
                            </Col>
                            <Col span={12}>
                              <Space size="small" align="center">
                                <BaseButtonsForm.Item
                                  name="isDropoff"
                                  valuePropName="checked"
                                  noStyle
                                >
                                  <Checkbox
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        form.setFieldsValue({
                                          isRma: false,
                                          isPickup: false,
                                        });
                                      }
                                    }}
                                  >
                                    {mlResponseString.dropOff}
                                  </Checkbox>
                                </BaseButtonsForm.Item>
                              </Space>
                            </Col>
                          </Row>
                        </Card>
                      </Col>
                    )}
                    {selectedRecord.billTo === "CARDTRONICS" && (
                      <Col span={24}>
                        <BaseButtonsForm.Item
                          name="isVessel"
                          valuePropName="checked"
                        >
                          <Checkbox>{mlResponseString.isVessel}</Checkbox>
                        </BaseButtonsForm.Item>
                      </Col>
                    )}
                    <Col span={24}>
                      <BaseButtonsForm.Item
                        name="specialInstruction"
                        label={mlResponseString.specialInstructions}
                      ></BaseButtonsForm.Item>
                    </Col>
                    <Col span={24}>
                      <BaseButtonsForm.Item name="specialInstruction">
                        <TextArea disabled={isModeUpdate} />
                      </BaseButtonsForm.Item>
                    </Col>
                    {selectedRecord.billTo === "NCRCORP" && (
                      <>
                        <Col span={24}>
                          <BaseButtonsForm.Item
                            name="pickupInstructions"
                            label={mlResponseString.pickupInstructions}
                          ></BaseButtonsForm.Item>
                        </Col>
                        <Col span={24}>
                          <BaseButtonsForm.Item name="pickupInstructions">
                            <TextArea disabled={isModeUpdate} />
                          </BaseButtonsForm.Item>
                        </Col>
                      </>
                    )}
                    {selectedRecord.billTo === "INTEGRATEDCASH" && (
                      <>
                        <Col span={24}>
                          <Card
                            size="small"
                            style={integratedCashCardStyle}
                            bodyStyle={integratedCashCardBodyStyle}
                          >
                            <Typography.Text
                              style={integratedCashSectionHeadingStyle}
                            >
                              {mlResponseString.isReturnTo}
                            </Typography.Text>
                            <Row gutter={12} style={integratedCashRowStyle}>
                              <Col span={12}>
                                <Space size="small" align="center">
                                  <BaseButtonsForm.Item
                                    name="isReturnToDE"
                                    valuePropName="checked"
                                    noStyle
                                  >
                                    <Checkbox
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          form.setFieldsValue({
                                            isReturnToNJ: false,
                                            isPickupFromDE: false,
                                            isPickupFromNJ: false,
                                          });
                                        }
                                      }}
                                    >
                                      {mlResponseString.de}
                                    </Checkbox>
                                  </BaseButtonsForm.Item>
                                </Space>
                              </Col>
                              <Col span={12}>
                                <Space size="small" align="center">
                                  <BaseButtonsForm.Item
                                    name="isReturnToNJ"
                                    valuePropName="checked"
                                    noStyle
                                  >
                                    <Checkbox
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          form.setFieldsValue({
                                            isReturnToDE: false,
                                            isPickupFromDE: false,
                                            isPickupFromNJ: false,
                                          });
                                        }
                                      }}
                                    >
                                      {mlResponseString.nj}
                                    </Checkbox>
                                  </BaseButtonsForm.Item>
                                </Space>
                              </Col>
                            </Row>
                          </Card>
                          <Card
                            size="small"
                            style={integratedCashCardStyle}
                            bodyStyle={integratedCashCardBodyStyle}
                          >
                            <Typography.Text
                              style={integratedCashSectionHeadingStyle}
                            >
                              {mlResponseString.isPickupFrom}
                            </Typography.Text>
                            <Row gutter={12} style={integratedCashRowStyle}>
                              <Col span={12}>
                                <Space size="small" align="center">
                                  <BaseButtonsForm.Item
                                    name="isPickupFromDE"
                                    valuePropName="checked"
                                    noStyle
                                  >
                                    <Checkbox
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          form.setFieldsValue({
                                            isReturnToDE: false,
                                            isReturnToNJ: false,
                                            isPickupFromNJ: false,
                                          });
                                        }
                                      }}
                                    >
                                      {mlResponseString.de}
                                    </Checkbox>
                                  </BaseButtonsForm.Item>
                                </Space>
                              </Col>
                              <Col span={12}>
                                <Space size="small" align="center">
                                  <BaseButtonsForm.Item
                                    name="isPickupFromNJ"
                                    valuePropName="checked"
                                    noStyle
                                  >
                                    <Checkbox
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          form.setFieldsValue({
                                            isReturnToDE: false,
                                            isReturnToNJ: false,
                                            isPickupFromDE: false,
                                          });
                                        }
                                      }}
                                    >
                                      {mlResponseString.nj}
                                    </Checkbox>
                                  </BaseButtonsForm.Item>
                                </Space>
                              </Col>
                            </Row>
                          </Card>
                          <Card
                            size="small"
                            style={integratedCashCardStyle}
                            bodyStyle={integratedCashCardBodyStyle}
                          >
                            <Typography.Text
                              style={integratedCashSectionHeadingStyle}
                            ></Typography.Text>
                            <Row gutter={12} style={integratedCashRowStyle}>
                              <Col span={12}>
                                <Space size="small" align="center">
                                  <BaseButtonsForm.Item
                                    name="isScrap"
                                    valuePropName="checked"
                                  >
                                    <Checkbox disabled={isModeUpdate}>
                                      {mlResponseString.scrap}
                                    </Checkbox>
                                  </BaseButtonsForm.Item>
                                </Space>
                              </Col>
                            </Row>
                          </Card>
                        </Col>
                      </>
                    )}
                    {selectedRecord.billTo === "LGELEC" && (
                      <Col span={24}>
                        <BaseButtonsForm.Item
                          name="isSpecialEquipment"
                          valuePropName="checked"
                        >
                          <Checkbox>
                            {mlResponseString.isSpecialEquipment}
                          </Checkbox>
                        </BaseButtonsForm.Item>
                      </Col>
                    )}
                  </Row>
                </Panel>
              </Collapse>
              <Collapse>
                <Panel
                  header="Shipper Information"
                  key="1"
                  style={{ backgroundColor: "#f1f5fc" }}
                  forceRender
                >
                  <Row gutter={{ xs: 10, md: 15, xl: 30 }}>
                    <Col span={24}>
                      <BaseButtonsForm.Item
                        name="shipFromContact"
                        label={<>{mlResponseString.shipContactName}</>}
                        labelCol={{ span: 24 }}
                        rules={[
                          {
                            required: false,
                            message: "Contact name is required",
                          },
                        ]}
                      >
                        <Input disabled={isModeUpdate} />
                      </BaseButtonsForm.Item>
                    </Col>
                    {(HPESpecific ||
                      selectedRecord.billTo === "OLYMPUS" ||
                      selectedRecord.billTo === "LGELEC" ||
                      selectedRecord.billTo === "LGELECMKTG" ||
                      selectedRecord.billTo === "SUPERIORP" ||
                      selectedRecord.billTo === "INTEGRATEDCASH" ||
                      selectedRecord.billTo === "CALIX") && (
                      <Col span={24}>
                        <BaseButtonsForm.Item
                          name="shipperCode"
                          label={<>{mlResponseString.shipperCode}</>}
                          labelCol={{ span: 24 }}
                          rules={[
                            {
                              required: false,
                            },
                          ]}
                        >
                          <Input disabled={isModeUpdate} />
                        </BaseButtonsForm.Item>
                      </Col>
                    )}
                    <Col span={24}>
                      <BaseButtonsForm.Item
                        name="shipFromName"
                        label={<>{mlResponseString.shipCompanyName}</>}
                        labelCol={{ span: 24 }}
                        rules={[
                          {
                            required: !isModeUpdate,
                            message: "Company name is required",
                          },
                        ]}
                      >
                        <Input disabled={isModeUpdate} />
                      </BaseButtonsForm.Item>
                    </Col>
                    {(selectedRecord.billTo === "HPE" ||
                      selectedRecord.billTo === "CALIXNETWORKS") && (
                      <Col span={24}>
                        <BaseButtonsForm.Item
                          name="shipperNo"
                          label={<>{mlResponseString.shipperNo}</>}
                          labelCol={{ span: 24 }}
                        >
                          <Input disabled={isModeUpdate} />
                        </BaseButtonsForm.Item>
                      </Col>
                    )}
                    <Col span={24}>
                      <BaseButtonsForm.Item
                        name="shipFromAddress1"
                        label={<>{mlResponseString.shipCustomerAddress}</>}
                        labelCol={{ span: 24 }}
                        rules={[
                          {
                            required: !isModeUpdate,
                            message: "Address1 is required",
                          },
                        ]}
                      >
                        <Input disabled={isModeUpdate} />
                      </BaseButtonsForm.Item>
                    </Col>
                    <Col span={24}>
                      <BaseButtonsForm.Item
                        name="shipFromAddress2"
                        label={<>{mlResponseString.shipCustomerAddress2}</>}
                        labelCol={{ span: 24 }}
                      >
                        <Input disabled={isModeUpdate} />
                      </BaseButtonsForm.Item>
                    </Col>
                    <Col span={24}>
                      <BaseButtonsForm.Item
                        name="shipFromCity"
                        label={<>{mlResponseString.shipCustomerCity}</>}
                        labelCol={{ span: 24 }}
                        rules={[
                          {
                            required: !isModeUpdate,
                            message: "City is required",
                          },
                        ]}
                      >
                        <Input disabled={isModeUpdate} />
                      </BaseButtonsForm.Item>
                    </Col>

                    <Col span={24}>
                      <BaseButtonsForm.Item
                        name="shipFromState"
                        label={<>{mlResponseString.shipCustomerState}</>}
                        labelCol={{ span: 24 }}
                        rules={[
                          {
                            required: !isModeUpdate,
                            message: "State is required",
                          },
                        ]}
                      >
                        <Input disabled={isModeUpdate} />
                      </BaseButtonsForm.Item>
                    </Col>

                    <Col span={24}>
                      <BaseButtonsForm.Item
                        name="shipFromZip"
                        label={<>{mlResponseString.shipCustomerZip}</>}
                        labelCol={{ span: 24 }}
                        rules={[
                          {
                            required: !isModeUpdate,
                            message: "Zip is required",
                          },
                          {
                            pattern:
                              /^(\d{5}|[A-Za-z]\d[A-Za-z][ ]?\d[A-Za-z]\d)$/,
                            message: "Zip must contain alphanumeric only",
                          },
                        ]}
                      >
                        <Input disabled={isModeUpdate} />
                      </BaseButtonsForm.Item>
                    </Col>
                    <Col span={24}>
                      <BaseButtonsForm.Item
                        name="shipFromCountry"
                        label={<>{mlResponseString.shipCustomerCountry}</>}
                        labelCol={{ span: 24 }}
                      >
                        {SelectCountryDropdown(
                          COUNTRY_CODES,
                          "United States",
                          undefined,
                          stringConfig.select,
                          isModeUpdate,
                        )}
                      </BaseButtonsForm.Item>
                    </Col>
                    <Col span={24}>
                      <BaseButtonsForm.Item
                        name="shipFromPhone"
                        label={<>{mlResponseString.shipCustomerPhone}</>}
                        labelCol={{
                          span: 24,
                          style: { minWidth: "120px", textAlign: "left" },
                        }}
                        rules={[
                          { required: false, message: "Phone is required" },
                        ]}
                      >
                        <PhoneInputComponent
                          form={form}
                          phoneData={mobileShipFromPhoneData}
                          setPhoneData={setMobileShipFromPhoneData}
                          country="us"
                          countriesList={["ca", "mx", "us"]}
                          fieldName="shipFromPhone"
                          isDisabled={isModeUpdate}
                        />
                      </BaseButtonsForm.Item>
                    </Col>
                    <Col span={24}>
                      <BaseButtonsForm.Item
                        name="shipFromEmail"
                        label={<>Email</>}
                        labelCol={{ span: 24 }}
                        rules={
                          isModeUpdate
                            ? []
                            : [
                                {
                                  type: "email",
                                  message: "Please enter a valid email address",
                                },
                              ]
                        }
                      >
                        <Input maxLength={200} disabled={isModeUpdate} />
                      </BaseButtonsForm.Item>
                    </Col>
                  </Row>
                </Panel>
              </Collapse>
              <Collapse>
                <Panel
                  header="Consignee Information"
                  key="1"
                  style={{ backgroundColor: "#f1f5fc" }}
                  forceRender
                >
                  <Row gutter={{ xs: 10, md: 15, xl: 30 }}>
                    <Col span={24}>
                      <BaseButtonsForm.Item
                        name="shipToCustomerContact"
                        label={<>{mlResponseString.shipContactName}</>}
                        labelCol={{ span: 24 }}
                        rules={[
                          {
                            required: false,
                            message: "Contact name is required",
                          },
                        ]}
                      >
                        <Input disabled={isModeUpdate} />
                      </BaseButtonsForm.Item>
                    </Col>
                    <Col span={24}>
                      <BaseButtonsForm.Item
                        name="shipToCustomerName"
                        label={<>{mlResponseString.shipCompanyName}</>}
                        labelCol={{ span: 24 }}
                        rules={[
                          {
                            required: !isModeUpdate,
                            message: "Company name is required",
                          },
                        ]}
                      >
                        <Input disabled={isModeUpdate} />
                      </BaseButtonsForm.Item>
                    </Col>
                    {(selectedRecord.billTo === "HPE" ||
                      selectedRecord.billTo === "CALIXNETWORKS") && (
                      <Col span={24}>
                        <BaseButtonsForm.Item
                          name="consigneeNo"
                          label={<>{mlResponseString.consigneeNo}</>}
                          labelCol={{ span: 24 }}
                        >
                          <Input disabled={isModeUpdate} />
                        </BaseButtonsForm.Item>
                      </Col>
                    )}
                    <Col span={24}>
                      <BaseButtonsForm.Item
                        name="shipToCustomerAddress1"
                        label={<>{mlResponseString.shipCustomerAddress}</>}
                        labelCol={{ span: 24 }}
                        rules={[
                          {
                            required: !isModeUpdate,
                            message: "Address1 is required",
                          },
                        ]}
                      >
                        <Input disabled={isModeUpdate} />
                      </BaseButtonsForm.Item>
                    </Col>
                    <Col span={24}>
                      <BaseButtonsForm.Item
                        name="shipToCustomerAddress2"
                        label={<>{mlResponseString.shipCustomerAddress2}</>}
                        labelCol={{ span: 24 }}
                      >
                        <Input disabled={isModeUpdate} />
                      </BaseButtonsForm.Item>
                    </Col>
                    <Col span={24}>
                      <BaseButtonsForm.Item
                        name="shipToCustomerCity"
                        label={<>{mlResponseString.shipCustomerCity}</>}
                        labelCol={{ span: 24 }}
                        rules={[
                          {
                            required: !isModeUpdate,
                            message: "City is required",
                          },
                        ]}
                      >
                        <Input disabled={isModeUpdate} />
                      </BaseButtonsForm.Item>
                    </Col>
                    <Col span={24}>
                      <BaseButtonsForm.Item
                        name="shipToCustomerState"
                        label={<>{mlResponseString.shipCustomerState}</>}
                        labelCol={{ span: 24 }}
                        rules={[
                          {
                            required: !isModeUpdate,
                            message: "State is required",
                          },
                        ]}
                      >
                        <Input disabled={isModeUpdate} />
                      </BaseButtonsForm.Item>
                    </Col>
                    <Col span={24}>
                      <BaseButtonsForm.Item
                        name="shipToCustomerZip"
                        label={<>{mlResponseString.shipCustomerZip}</>}
                        labelCol={{ span: 24 }}
                        rules={[
                          {
                            required: !isModeUpdate,
                            message: "Zip is required",
                          },
                          {
                            pattern:
                              /^(\d{5}|[A-Za-z]\d[A-Za-z][ ]?\d[A-Za-z]\d)$/,
                            message: "Zip must contain alphanumeric only",
                          },
                        ]}
                      >
                        <Input disabled={isModeUpdate} />
                      </BaseButtonsForm.Item>
                    </Col>
                    <Col span={24}>
                      <BaseButtonsForm.Item
                        name="shipToCustomerCountry"
                        label={<>{mlResponseString.shipCustomerCountry}</>}
                        labelCol={{ span: 24 }}
                      >
                        {SelectCountryDropdown(
                          COUNTRY_CODES,
                          "United States",
                          undefined,
                          stringConfig.select,
                          isModeUpdate,
                        )}
                      </BaseButtonsForm.Item>
                    </Col>
                    <Col span={24}>
                      <BaseButtonsForm.Item
                        name="shipToCustomerPhone"
                        label={<>{mlResponseString.shipCustomerPhone}</>}
                        rules={[
                          { required: false, message: "Phone is required" },
                        ]}
                        labelCol={{
                          span: 24,
                          style: { minWidth: "120px", textAlign: "left" },
                        }}
                      >
                        <PhoneInputComponent
                          form={form}
                          phoneData={mobileShipToPhoneData}
                          setPhoneData={setMobileShipToPhoneData}
                          country="us"
                          countriesList={["ca", "mx", "us"]}
                          fieldName="shipToCustomerPhone"
                          isDisabled={isModeUpdate}
                        />
                      </BaseButtonsForm.Item>
                    </Col>
                    <Col span={24}>
                      <BaseButtonsForm.Item
                        name="shipToCustomerEmail"
                        label={<>{mlResponseString.shipCustomerEmail}</>}
                        labelCol={{ span: 24 }}
                        rules={
                          isModeUpdate
                            ? []
                            : [
                                {
                                  type: "email",
                                  message: "Please enter a valid email address",
                                },
                              ]
                        }
                      >
                        <Input maxLength={200} disabled={isModeUpdate} />
                      </BaseButtonsForm.Item>
                    </Col>
                  </Row>
                </Panel>
              </Collapse>
              <Collapse>
                <Panel
                  header="Line Items"
                  key="1"
                  style={{ backgroundColor: "#f1f5fc" }}
                  forceRender
                >
                  <>
                    <Row gutter={{ xs: 10, md: 15, xl: 30 }}>
                      <Col span={24}>
                        <BaseButtonsForm.Item
                          label={mlResponseString.hazmat}
                          name="hazmat"
                        ></BaseButtonsForm.Item>
                      </Col>
                      <Col span={24}>
                        <BaseButtonsForm.Item
                          name="isHazmat"
                          valuePropName="checked"
                        >
                          <Checkbox>
                            {
                              mlResponseString.thisShipmentContainHazardousMaterial
                            }
                          </Checkbox>
                        </BaseButtonsForm.Item>
                      </Col>
                    </Row>
                    {isHazmatChecked && selectedRecord.billTo === "HPE" && (
                      <>
                        <Col span={24}>
                          <BaseButtonsForm.Item
                            name="goodsName"
                            label={<>{mlResponseString.goodsName}</>}
                            labelCol={{ span: 24 }}
                          >
                            <Input disabled={isModeUpdate} />
                          </BaseButtonsForm.Item>
                        </Col>
                        <Col span={24}>
                          <BaseButtonsForm.Item
                            name="goodsClass"
                            label={<>{mlResponseString.goodsClass}</>}
                            labelCol={{ span: 24 }}
                          >
                            <Input disabled={isModeUpdate} />
                          </BaseButtonsForm.Item>
                        </Col>
                        <Col span={24}>
                          <BaseButtonsForm.Item
                            name="emergencyContact"
                            label={<>{mlResponseString.emergencyContact}</>}
                            labelCol={{ span: 24 }}
                          >
                            <Input disabled={isModeUpdate} />
                          </BaseButtonsForm.Item>
                        </Col>
                      </>
                    )}
                    <div className="lineitem">
                      <Button
                        type="primary"
                        onClick={addItem}
                        icon={<PlusOutlined style={{ cursor: "pointer" }} />}
                        style={{ marginTop: "20px" }}
                        disabled={
                          (selectedRecord.mlStatus === "Success" &&
                            (selectedRecord.tmsStatus === "Success" ||
                              selectedRecord.tmsStatus === "Resolved")) ||
                          selectedRecord.mlStatus === "New" ||
                          selectedRecord.mlStatus === "Processing"
                        }
                      >
                        Add Line Item
                      </Button>
                      {itemsLength > 0 && (
                        <Form.List name="items">
                          {(fields, { remove }) => (
                            <Collapse>
                              {fields?.map((field, index) => (
                                <Panel
                                  header={
                                    <div className="header">
                                      <Form.Item
                                        noStyle
                                        name={[field.name, "itemName"]}
                                      >
                                        <Input
                                          placeholder={`Item ${index + 1}`}
                                          style={{
                                            background: "transparent",
                                            border: "1px solid transparent",
                                          }}
                                        />
                                      </Form.Item>
                                      <DeleteOutlined
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDelete(remove, field.name);
                                        }}
                                        style={{ cursor: "pointer" }}
                                      />
                                    </div>
                                  }
                                  key={field.key}
                                  forceRender
                                >
                                  <Row gutter={{ xs: 10, md: 15, xl: 30 }}>
                                    <Col span={24}>
                                      <BaseButtonsForm.Item
                                        {...field}
                                        name={[field.name, "itemName"]}
                                        label={<>{mlItemsString.name}</>}
                                        labelCol={{ span: 24 }}
                                        rules={[
                                          {
                                            required: false,
                                            message: "Name is required",
                                          },
                                        ]}
                                      >
                                        <Input />
                                      </BaseButtonsForm.Item>
                                    </Col>
                                    <Col span={24}>
                                      <BaseButtonsForm.Item
                                        {...field}
                                        name={[field.name, "itemDescription"]}
                                        label={<>{mlItemsString.description}</>}
                                        labelCol={{ span: 24 }}
                                        rules={[
                                          {
                                            required: true,
                                            message: "Description is required",
                                          },
                                        ]}
                                      >
                                        <Input />
                                      </BaseButtonsForm.Item>
                                    </Col>
                                    <Col span={24}>
                                      <BaseButtonsForm.Item
                                        {...field}
                                        name={[field.name, "itemQuantity"]}
                                        label={<>{mlItemsString.quantity}</>}
                                        labelCol={{ span: 24 }}
                                        rules={[
                                          {
                                            required: true,
                                            message: "Quantity is required",
                                          },
                                        ]}
                                      >
                                        <Input />
                                      </BaseButtonsForm.Item>
                                    </Col>
                                    <Col span={24}>
                                      <BaseButtonsForm.Item
                                        {...field}
                                        name={[field.name, "itemContainerType"]}
                                        label={
                                          <>{mlItemsString.containerType}</>
                                        }
                                        labelCol={{ span: 24 }}
                                        rules={[
                                          {
                                            required: true,
                                            message: "Container is required",
                                          },
                                        ]}
                                      >
                                        {SelectContainerDropdown(
                                          containerTypes,
                                          "crates",
                                          undefined,
                                          stringConfig.select,
                                        )}
                                      </BaseButtonsForm.Item>
                                    </Col>

                                    {isModeUpdate ? (
                                      <Col span={24}>
                                        <BaseButtonsForm.Item
                                          {...field}
                                          name={[field.name, "itemWeight"]}
                                          label={<>{mlItemsString.weight}</>}
                                          labelCol={{ span: 24 }}
                                          rules={[
                                            {
                                              required: true,
                                              message: "Weight is required",
                                            },
                                            {
                                              type: "number",
                                              min: 0.1,
                                              message:
                                                "Weight must be at least 0.1",
                                              transform: (value) =>
                                                Number(value),
                                            },
                                          ]}
                                        >
                                          <Input />
                                        </BaseButtonsForm.Item>
                                      </Col>
                                    ) : (
                                      <Col span={24}>
                                        <BaseButtonsForm.Item
                                          {...field}
                                          name={[field.name, "itemWeight"]}
                                          label={<>{mlItemsString.weight}</>}
                                          labelCol={{ span: 24 }}
                                          rules={[
                                            {
                                              required: true,
                                              message: "Weight is required",
                                            },
                                            {
                                              type: "number",
                                              min: 1,
                                              message:
                                                "Weight must be at least 1",
                                              transform: (value) =>
                                                Number(value),
                                            },
                                          ]}
                                        >
                                          <Input />
                                        </BaseButtonsForm.Item>
                                      </Col>
                                    )}
                                    <Col span={24}>
                                      <BaseButtonsForm.Item
                                        {...field}
                                        name={[field.name, "itemWeightUnit"]}
                                        label={<>{mlItemsString.weightUnit}</>}
                                        labelCol={{ span: 24 }}
                                        rules={[
                                          {
                                            required: true,
                                            message: "Weight unit is required",
                                          },
                                        ]}
                                      >
                                        {SelectWeightDropdown(
                                          weightTypes,
                                          "lb",
                                          undefined,
                                          stringConfig.select,
                                          isModeUpdate,
                                        )}
                                      </BaseButtonsForm.Item>
                                    </Col>
                                    <Col span={24}>
                                      <BaseButtonsForm.Item
                                        {...field}
                                        name={[field.name, "itemLength"]}
                                        label={<>{mlItemsString.length}</>}
                                        labelCol={{ span: 24 }}
                                        required={false}
                                      >
                                        <Input />
                                      </BaseButtonsForm.Item>
                                    </Col>
                                    <Col span={24}>
                                      <BaseButtonsForm.Item
                                        {...field}
                                        name={[field.name, "itemWidth"]}
                                        label={<>{mlItemsString.width}</>}
                                        labelCol={{ span: 24 }}
                                        required={false}
                                      >
                                        <Input />
                                      </BaseButtonsForm.Item>
                                    </Col>
                                    <Col span={24}>
                                      <BaseButtonsForm.Item
                                        {...field}
                                        name={[field.name, "itemHeight"]}
                                        label={<>{mlItemsString.height}</>}
                                        labelCol={{ span: 24 }}
                                        required={false}
                                      >
                                        <Input />
                                      </BaseButtonsForm.Item>
                                    </Col>
                                    <Col span={24}>
                                      <BaseButtonsForm.Item
                                        {...field}
                                        name={[field.name, "itemDimensionUnit"]}
                                        labelCol={{ span: 24 }}
                                        label={
                                          <div
                                            style={{
                                              display: "flex",
                                              flexDirection: "column",
                                              alignItems: "flex-start",
                                            }}
                                          >
                                            <div>
                                              {mlItemsString.dimensionUnit}
                                            </div>
                                            <div>
                                              {mlItemsString.dimensions}
                                            </div>
                                          </div>
                                        }
                                        required={false}
                                      >
                                        {SelectDimensionsDropdown(
                                          dimensionTypes,
                                          "in",
                                          undefined,
                                          stringConfig.select,
                                          isModeUpdate,
                                        )}
                                      </BaseButtonsForm.Item>
                                    </Col>
                                    {selectedRecord.billTo ===
                                      "CARDTRONICS" && (
                                      <Col span={24}>
                                        <BaseButtonsForm.Item
                                          {...field}
                                          name={[field.name, "isHazmat"]}
                                          valuePropName="checked"
                                        >
                                          <Checkbox
                                            onChange={(e) => {
                                              if (e.target.checked) {
                                                form.setFieldsValue({
                                                  isHazmat: true,
                                                });
                                              }
                                            }}
                                          >
                                            {mlResponseString.hazmat}
                                          </Checkbox>
                                        </BaseButtonsForm.Item>
                                      </Col>
                                    )}
                                  </Row>
                                </Panel>
                              ))}
                            </Collapse>
                          )}
                        </Form.List>
                      )}
                    </div>
                  </>
                </Panel>
              </Collapse>
            </PDFFormWrapper>
          </FormContainer>
        </BaseButtonsForm>
        {isClose ? (
          <div
            style={{
              display: "flex",
              marginTop: "10px",
              justifyContent: "flex-end",
            }}
          >
            <Button
              key="back"
              type="primary"
              onClick={handleCancel}
              style={{
                fontSize: FONT_SIZE_PX.xs,
                marginRight: 23,
                minWidth: 100,
              }}
            >
              Close
            </Button>
          </div>
        ) : (
          hasEditAccess && (
            <div
              style={{
                display: "flex",
                marginTop: "10px",
                justifyContent: "flex-end",
                gap: "5px",
              }}
            >
              <Button
                key="submit"
                type="primary"
                htmlType="submit"
                form="pdfForm"
                style={{
                  fontSize: FONT_SIZE_PX.xs,
                  marginRight: 23,
                  minWidth: 100,
                }}
              >
                {saveText}
              </Button>
              <Button
                key="back"
                type="primary"
                onClick={handleCancel}
                style={{
                  fontSize: FONT_SIZE_PX.xs,
                  marginRight: 23,
                  minWidth: 100,
                }}
              >
                {cancelText}
              </Button>
            </div>
          )
        )}
      </PDFFormContainer>
    </>
  );
};

export default PdfForm;
